import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

interface PageHeroProps {
  eyebrow: string;
  title: string;
  description: string;
  breadcrumb: string;
  children?: ReactNode;
}

export default function PageHero({ eyebrow, title, description, breadcrumb, children }: PageHeroProps) {
  return (
    <section className="relative w-full overflow-hidden bg-foreground-950 pt-28 pb-14 md:pt-40 md:pb-20">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(var(--background-50)) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--background-50)) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-primary-500/20 blur-3xl" />

      <div className="relative z-10 mx-auto w-full max-w-container px-4 md:px-6">
        <motion.nav
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          aria-label="Breadcrumb"
          className="flex items-center gap-2 font-label text-[11px] font-medium uppercase tracking-[0.16em] text-background-300"
        >
          <Link to="/" className="transition-colors hover:text-primary-300">
            Home
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-primary-300">{breadcrumb}</span>
        </motion.nav>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-6 max-w-3xl"
        >
          <div className="flex items-center gap-3 font-label text-xs font-semibold uppercase tracking-[0.22em] text-primary-300">
            <span className="h-px w-8 bg-current opacity-60" />
            {eyebrow}
          </div>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            {title}
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-background-200 md:text-base">
            {description}
          </p>
          {children ? <div className="mt-8">{children}</div> : null}
        </motion.div>
      </div>
    </section>
  );
}